import { useEffect, useRef } from "react";

/**
 * useAutoReload — call the given reload fn whenever any mutation (POST/PUT/DELETE/PATCH)
 * fires the global `kh:data-mutated` event (dispatched by the axios interceptor in
 * lib/api.js). Debounced ~300ms so a burst of edits triggers only one reload.
 *
 * Usage:
 *   const load = useCallback(() => api.get(...).then(r => setRows(r.data)), []);
 *   useEffect(() => { load(); }, [load]);
 *   useAutoReload(load);
 */
export function useAutoReload(reload) {
  const ref = useRef(reload);
  ref.current = reload;
  useEffect(() => {
    let t = null;
    const onMut = () => {
      clearTimeout(t);
      t = setTimeout(() => { try { ref.current?.(); } catch { /* ignore */ } }, 300);
    };
    window.addEventListener("kh:data-mutated", onMut);
    return () => { window.removeEventListener("kh:data-mutated", onMut); clearTimeout(t); };
  }, []);
}
